import { forwardRef, type HTMLProps, type LegacyRef } from 'react';
import assert from 'assert';
import type {
  ApiCampaign,
  ApiReturnSite,
  ApiReturnThematic,
} from '@highimpact-frontend/api-types';
import { useAppSelector } from '../../store/hooks';
import { isDataState } from '../../store/data/state';
import StateDecorator from '../utils/state/StateDecorator';
import 'react-pure-modal/dist/react-pure-modal.min.css';
import { createValidator, useForm } from '../../utils/hooks/useForm';
import CampaignService from '../../api/services/config/campaign/CampaignService';
import {
  changeHourToEndOfDay,
  changeHourToStartOfDay,
  formatDateToLocalString,
} from '../../utils/date';
import { useSubmit } from '../../utils/hooks/useSubmit';
import CustomizedDialog from '../utils/MUISpecifics/dialog/CustumizedDialog';
import { Button, Stack } from '@mui/material';
import TextInput from '../utils/MUISpecifics/inputText/TextInput';
import DropdownItemsSelector from '../utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import SaveIcon from '@mui/icons-material/Save';
import DateTimeRangePickerComponent from '../utils/MUISpecifics/DateTimeRangePicker';
import { DateRange } from 'rsuite/esm/DateRangePicker/types';

export interface FormProps extends HTMLProps<HTMLInputElement> {
  onCancel: () => void;
  isOpen: boolean;
  sites: ApiReturnSite[];
  thematics: ApiReturnThematic[];
  refresh: () => void;
}

const defaultValues: ApiCampaign = {
  name: '',
  start_date: '',
  end_date: '',
  site_ids: [],
  thematic_ids: [],
};

// Component.
function CreateCampaignForm(
  {
    isOpen: isModalOpen,
    onCancel,
    sites,
    thematics,
    refresh,
    ...props
  }: FormProps,
  ref: LegacyRef<HTMLDivElement> | undefined
) {
  const projects = useAppSelector(
    (state) => state.data_slice.externals.projects
  );

  const { submit, state } = useSubmit();

  // Handle form submission.
  const onSubmit = async (data: ApiCampaign) => {
    data.site_ids = [sites[0].id]; // FIXME: Another clean way to get site_id before submit ?
    submit({
      callbacks: [
        async () => await CampaignService.add(data),
        () => methods.reset(),
        () => onCancel(),
        async () => refresh(),
      ],
      errorMessages: {
        anyError: 'Une erreur est survenue lors de la création de la campagne.',
        unicityError:
          'Une campagne avec les mêmes nom, thématique et période existe déjà pour ce site',
      },
      successMessage: 'La campagne a été créée avec succès',
    });
  };

  /**
   * Create validation rule for each field.
   */
  const validator = createValidator<ApiCampaign>({
    name: [(n) => (n.length <= 3 ? 'Entrer au moins 4 caractères' : undefined)],
    start_date: [
      (n) => {
        if (!n || n === '' || n === null) return 'La date est requise';
        return isNaN(Date.parse(n)) ? 'La date doit être valide' : undefined;
      },
    ],
    end_date: [
      (n) => {
        if (!n || n === '' || n === null) return 'La date est requise';
        return isNaN(Date.parse(n)) ? 'La date doit être valide' : undefined;
      },
    ],
    site_ids: [],
    thematic_ids: [
      (n) => (!n || n.length === 0 ? 'Sélectionner une thématique' : undefined),
    ],
  });

  // Define form hook.
  const methods = useForm(defaultValues, validator, onSubmit);

  const handleDateUpdate = (value: DateRange | null) => {
    if (!value || !value[0] || !value[1]) {
      // If a user has cleared or not selected a range
      methods.setMultipleValues({
        start_date: '',
        end_date: '',
      });
      return;
    }
    methods.setMultipleValues({
      start_date: formatDateToLocalString(changeHourToStartOfDay(value[0])),
      end_date: formatDateToLocalString(changeHourToEndOfDay(value[1])),
    });
  };

  return StateDecorator([projects], ref!, () => {
    assert(isDataState(projects));

    return (
      <CustomizedDialog
        title="Créer une nouvelle campagne"
        open={isModalOpen}
        onCancel={() => {
          onCancel();
          methods.reset();
        }}
      >
        <form
          onSubmit={methods.onSubmit}
          className={`form ${props.className || ''}`}
        >
          <Stack spacing={2}>
            <TextInput
              onChange={(value) => {
                methods.setValue('name', value);
              }}
              label={'Nom'}
              name={'name'}
              placeholder="Entrer un nom"
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('name') }
                  : { tag: 'Not submitted' }
              }
              required
            />

            <DropdownItemsSelector
              onSelect={(selectedId) =>
                methods.setValue('thematic_ids', [selectedId!])
              }
              values={thematics}
              label={'Thématique'}
              disabled={false}
              name={'thematic_name'}
              errors={
                methods.hasSubmitted
                  ? {
                      tag: 'Submited',
                      errors: methods.getErrors('thematic_ids'),
                    }
                  : { tag: 'Not submitted' }
              }
              required
            />

            <DateTimeRangePickerComponent
              label="Période"
              format="DD/MM/YYYY"
              onChange={handleDateUpdate}
              error={
                methods.getErrors('start_date').length > 0 ||
                methods.getErrors('end_date').length > 0
              }
              helperText={
                methods.getErrors('start_date')[0] ||
                methods.getErrors('end_date')[0] ||
                ''
              }
              safeDefault={false}
            />

            <Button
              type="submit"
              className="form-button-submit"
              variant="outlined"
              loadingPosition="end"
              endIcon={<SaveIcon />}
            >
              {state === 'Loading' ? 'Envoi...' : 'Créer'}
            </Button>
          </Stack>
        </form>
      </CustomizedDialog>
    );
  });
}

export default forwardRef(CreateCampaignForm);
